import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Building2, Plus, Loader2, Copy, Trash2, Pencil, Check, X, Link2, Smartphone,
} from "lucide-react";
import api from "@/lib/api";

export default function AgencyPanel() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(null);
  const [name, setName] = useState("");
  const [lines, setLines] = useState(1);
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");
  const [editLines, setEditLines] = useState(0);
  const [brandName, setBrandName] = useState("");
  const [brandLogo, setBrandLogo] = useState("");
  const [savingBrand, setSavingBrand] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const { data } = await api.get("/agency/subaccounts");
      setData(data);
      setForbidden(null);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setForbidden(typeof detail === "string" ? detail : "Tu cuenta no incluye subcuentas");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchBrand = useCallback(async () => {
    try {
      const { data } = await api.get("/my-brand");
      if (data?.brand) {
        setBrandName(data.brand.name || "");
        setBrandLogo(data.brand.logo_url || "");
      }
    } catch { /* cosmetic */ }
  }, []);

  useEffect(() => {
    fetchBrand();
  }, [fetchBrand]);

  const saveBrand = async () => {
    setSavingBrand(true);
    try {
      await api.put("/agency/brand", { name: brandName, logo_url: brandLogo });
      toast.success(t("agency.toastBrandSaved"));
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo guardar la marca");
    } finally {
      setSavingBrand(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const copy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success(t("agency.toastLinkCopied"));
  };

  const showError = (err, fallback) => {
    const detail = err.response?.data?.detail;
    const msg = (detail && typeof detail === "object" && detail.message) || (typeof detail === "string" && detail) || fallback;
    toast.error(msg, { duration: 8000 });
    const links = detail && typeof detail === "object" && detail.payment_links;
    if (links) {
      toast.info(t("agency.toastExtendPlan"), {
        duration: 10000,
        action: { label: "Comprar extra", onClick: () => window.open(links.extra_subaccount || links.extra_number, "_blank") },
      });
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!name.trim()) return toast.error("El nombre es requerido");
    setCreating(true);
    try {
      await api.post("/agency/subaccounts", { name: name.trim(), assigned_lines: Number(lines) || 0 });
      toast.success("Subcuenta creada");
      setName("");
      setLines(1);
      fetchData();
    } catch (err) {
      showError(err, "No se pudo crear la subcuenta");
    } finally {
      setCreating(false);
    }
  };

  const startEdit = (sub) => {
    setEditingId(sub.id);
    setEditName(sub.name);
    setEditLines(sub.assigned_lines);
  };

  const handleSaveEdit = async (id) => {
    try {
      await api.put(`/agency/subaccounts/${id}`, { name: editName, assigned_lines: Number(editLines) || 0 });
      toast.success("Subcuenta actualizada");
      setEditingId(null);
      fetchData();
    } catch (err) {
      showError(err, "No se pudo actualizar");
    }
  };

  const handleDelete = async (sub) => {
    if (!window.confirm(`¿Eliminar la subcuenta "${sub.name}"? Esta acción no se puede deshacer.`)) return;
    try {
      await api.delete(`/agency/subaccounts/${sub.id}`);
      toast.success("Subcuenta eliminada");
      fetchData();
    } catch (err) {
      showError(err, "No se pudo eliminar");
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center gap-2 text-muted-foreground">
        <Loader2 className="w-5 h-5 animate-spin" /> {t("agency.loading")}
      </div>
    );
  }

  if (forbidden) {
    return (
      <div className="p-6">
        <Card className="max-w-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-primary" /> {t("nav.agency")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground mb-4">{forbidden}</p>
            <p className="text-sm text-muted-foreground">
              {t("agency.forbiddenNote")}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="agency-panel">
      {/* Whitelabel brand: what the agency's end clients see in their embedded panel */}
      <Card data-testid="brand-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Link2 className="w-5 h-5 text-primary" /> {t("agency.whitelabel")}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">
            {t("agency.whitelabelNote")}
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="brand-name">{t("agency.brandName")}</Label>
              <Input id="brand-name" value={brandName} onChange={(e) => setBrandName(e.target.value)}
                placeholder={t("agency.brandNamePlaceholder")} data-testid="brand-name-input" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="brand-logo">{t("agency.brandLogo")}</Label>
              <Input id="brand-logo" value={brandLogo} onChange={(e) => setBrandLogo(e.target.value)}
                placeholder="https://... (PNG cuadrado)" data-testid="brand-logo-input" />
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button size="sm" onClick={saveBrand} disabled={savingBrand} data-testid="brand-save-btn">
              {savingBrand ? t("agency.saving") : t("agency.saveBrand")}
            </Button>
            {(brandLogo || brandName) && (
              <span className="text-xs text-muted-foreground flex items-center gap-2">
                {t("agency.preview")}
                {brandLogo && <img src={brandLogo} alt="logo" className="w-6 h-6 rounded object-cover" />}
                <strong>{brandName || "LeadBot"}</strong>
              </span>
            )}
          </div>
        </CardContent>
      </Card>

      <div>
        <h1 className="text-2xl font-bold text-foreground">{t("nav.agency")}</h1>
        <p className="text-muted-foreground">
          {t("agency.subtitle")}
        </p>
      </div>

      {/* Resumen */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm font-medium text-muted-foreground">{t("agency.subaccounts")}</CardTitle></CardHeader>
          <CardContent><div className="text-2xl font-bold">{data.subaccounts_used} / {data.max_businesses}</div></CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm font-medium text-muted-foreground">{t("agency.linesAssigned")}</CardTitle></CardHeader>
          <CardContent><div className="text-2xl font-bold">{data.lines_assigned} / {data.max_sessions}</div></CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm font-medium text-muted-foreground">{t("agency.linesAvailable")}</CardTitle></CardHeader>
          <CardContent><div className="text-2xl font-bold text-green-600">{data.lines_available}</div></CardContent>
        </Card>
      </div>

      {/* Crear */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Plus className="w-5 h-5 text-primary" /> {t("agency.newSub")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleCreate} className="flex flex-wrap items-end gap-4">
            <div className="space-y-1 flex-1 min-w-[200px]">
              <Label htmlFor="sub-name">{t("agency.clientName")}</Label>
              <Input id="sub-name" value={name} onChange={(e) => setName(e.target.value)} placeholder={t("agency.clientNamePlaceholder")} />
            </div>
            <div className="space-y-1 w-40">
              <Label htmlFor="sub-lines">{t("agency.whatsappLines")}</Label>
              <Input id="sub-lines" type="number" min="0" value={lines} onChange={(e) => setLines(e.target.value)} />
            </div>
            <Button type="submit" disabled={creating}>
              {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
              {t("agency.createSub")}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Lista */}
      <div className="space-y-3">
        {data.subaccounts.length === 0 && (
          <p className="text-muted-foreground text-sm">{t("agency.noSubs")}</p>
        )}
        {data.subaccounts.map((sub) => (
          <Card key={sub.id}>
            <CardContent className="py-4 flex flex-wrap items-center gap-4 justify-between">
              {editingId === sub.id ? (
                <div className="flex flex-wrap items-end gap-3 flex-1">
                  <div className="space-y-1 min-w-[180px]">
                    <Label>{t("agency.name")}</Label>
                    <Input value={editName} onChange={(e) => setEditName(e.target.value)} />
                  </div>
                  <div className="space-y-1 w-32">
                    <Label>{t("agency.lines")}</Label>
                    <Input type="number" min="0" value={editLines} onChange={(e) => setEditLines(e.target.value)} />
                  </div>
                  <Button size="sm" onClick={() => handleSaveEdit(sub.id)}><Check className="w-4 h-4" /></Button>
                  <Button size="sm" variant="outline" onClick={() => setEditingId(null)}><X className="w-4 h-4" /></Button>
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-3 min-w-[220px]">
                    <Building2 className="w-5 h-5 text-primary shrink-0" />
                    <div>
                      <p className="font-semibold">{sub.name}</p>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Smartphone className="w-3.5 h-3.5" />
                        {sub.sessions_used} / {sub.assigned_lines} {t("agency.linesInUse")}
                        <Badge variant={sub.status === "active" ? "default" : "secondary"} className="text-xs">{sub.status}</Badge>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    {sub.activation_url && (
                      <Button variant="outline" size="sm" onClick={() => copy(sub.activation_url)}>
                        <Link2 className="w-4 h-4 mr-1" /> {t("agency.copyActivation")}
                      </Button>
                    )}
                    <Button variant="ghost" size="sm" onClick={() => startEdit(sub)}>
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(sub)}>
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      <Card className="border-dashed">
        <CardContent className="py-4 text-sm text-muted-foreground">
          <strong className="text-foreground">{t("agency.howToTitle")}</strong> {t("agency.howTo")}
        </CardContent>
      </Card>
    </div>
  );
}
